import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface TransactionStatusDisplayProps {
  transaction: any;
  status: any;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCopyAddress: (address: string) => void | Promise<void>;
  isAutoRefreshEnabled: boolean;
}

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  new: { label: 'Preparing', color: '#f59e0b', icon: '⏳' },
  waiting: { label: 'Awaiting Deposit', color: '#f59e0b', icon: '⏳' },
  confirming: { label: 'Confirming', color: '#818cf8', icon: '⛏' },
  exchanging: { label: 'Exchanging', color: '#818cf8', icon: '🔄' },
  sending: { label: 'Sending', color: '#22d3ee', icon: '📤' },
  verifying: { label: 'Verifying', color: '#818cf8', icon: '⛨' },
  finished: { label: 'Complete', color: '#10b981', icon: '✓' },
  completed: { label: 'Complete', color: '#10b981', icon: '✓' },
  done: { label: 'Complete', color: '#10b981', icon: '✓' },
  failed: { label: 'Failed', color: '#f43f5e', icon: '✕' },
  refunding: { label: 'Refunding', color: '#f59e0b', icon: '↩' },
  refunded: { label: 'Refunded', color: '#f59e0b', icon: '↩' },
  expired: { label: 'Expired', color: '#f43f5e', icon: '⏰' }
};

const REFRESH_INTERVAL = 10;

export const TransactionStatusDisplay: React.FC<TransactionStatusDisplayProps> = ({
  transaction,
  status,
  isLoading,
  error,
  onRefresh,
  onCopyAddress,
  isAutoRefreshEnabled
}) => {
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [copied, setCopied] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    setCountdown(REFRESH_INTERVAL);
    if (!isAutoRefreshEnabled || isLoading) return;
    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isAutoRefreshEnabled, isLoading]);

  if (!transaction) return null;

  const completeTransactionData = { ...transaction, ...(status || {}) };
  const currentStatus = completeTransactionData.status;
  const normalizedStatus = (currentStatus || '').toLowerCase();
  const statusInfo = statusConfig[normalizedStatus] || { label: 'Unknown', color: '#8892b0', icon: '?' };

  const isCompleted = ['finished', 'completed', 'done'].includes(normalizedStatus);
  const isFailed = ['failed', 'expired'].includes(normalizedStatus);
  const isPending = ['new', 'waiting', 'confirming', 'exchanging', 'sending', 'verifying'].includes(normalizedStatus);

  const payinAddress = completeTransactionData.payinAddress || completeTransactionData.changeNowData?.payinAddress || completeTransactionData.changeNowData?.payin_address;
  const payinExtraId = completeTransactionData.payinExtraId;
  const payinHash = completeTransactionData.payinHash || completeTransactionData.payinHashes?.[0] || completeTransactionData.transactions?.in?.[0]?.hash || completeTransactionData.changeNowData?.payinHash;
  const payoutHash = completeTransactionData.payoutHash || completeTransactionData.payoutHashes?.[0] || completeTransactionData.transactions?.out?.[0]?.hash || completeTransactionData.changeNowData?.payoutHash;
  const payoutAddress = completeTransactionData.payoutAddress || completeTransactionData.changeNowData?.payoutAddress;
  const createdAt = completeTransactionData.createdAt;
  const updatedAt = completeTransactionData.updatedAt;
  const depositReceivedAt = completeTransactionData.depositReceivedAt;
  const depositReceived = typeof completeTransactionData.depositReceived === 'boolean'
    ? completeTransactionData.depositReceived
    : Boolean(depositReceivedAt || payinHash || ['confirming', 'exchanging', 'sending', 'finished', 'completed', 'done', 'refunding', 'refunded', 'verifying'].includes(normalizedStatus));
  const payoutSent = typeof completeTransactionData.payoutSent === 'boolean'
    ? completeTransactionData.payoutSent
    : Boolean(payoutHash || isCompleted);
  const needsDeposit = ['new', 'waiting'].includes(normalizedStatus) && !depositReceived;

  const fromCurrency = completeTransactionData.fromCurrency || transaction.fromCurrency;
  const toCurrency = completeTransactionData.toCurrency || transaction.toCurrency;
  const expectedAmountFrom = completeTransactionData.expectedAmountFrom ?? transaction.fromAmount;
  const expectedAmountTo = completeTransactionData.expectedAmountTo ?? transaction.toAmount ?? transaction.toAmountEstimated;
  const amountFrom = completeTransactionData.amountFrom;
  const amountTo = completeTransactionData.amountTo;
  const displayAmountFrom = amountFrom ?? expectedAmountFrom;
  const displayAmountTo = amountTo ?? expectedAmountTo;
  const sessionTxId = transaction.id || completeTransactionData.id;

  const handleCopy = async (text: string) => {
    setCopyError(null);
    try {
      await onCopyAddress(text);
      setCopied(text);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
      setCopyError('Could not copy to the clipboard. Select and copy the value manually.');
    }
  };

  // Progress steps
  const steps = ['Deposit', 'Confirming', 'Exchanging', 'Sending', 'Complete'];
  const stepIndex = needsDeposit ? 0
    : ['confirming', 'verifying'].includes(normalizedStatus) ? 1
    : normalizedStatus === 'exchanging' ? 2
    : normalizedStatus === 'sending' ? 3
    : isCompleted ? 4 : -1;

  return (
    <div className="status-display">
      {/* Status Badge */}
      <div className="status-badge" style={{ borderColor: statusInfo.color + '40' }}>
        <span className="status-icon">{statusInfo.icon}</span>
        <div className="status-info" role="status">
          <div className="status-label" style={{ color: statusInfo.color }}>{statusInfo.label}</div>
          <div className="status-txid">TX {sessionTxId}</div>
        </div>
        <div className="status-timer">
          <span className="timer-dot" style={{ background: isAutoRefreshEnabled ? '#10b981' : '#4a5568' }}></span>
          <span className="timer-text">
            {!isAutoRefreshEnabled ? 'Paused' : isLoading ? 'Refreshing...' : `${countdown}s`}
          </span>
        </div>
      </div>

      {/* Session TXID — full, copyable */}
      {sessionTxId && (
        <div className="tx-hashes" style={{ marginTop: 12 }}>
          <div className="tx-hash-row">
            <span className="tx-hash-label">Session TXID</span>
            <button
              type="button"
              className="tx-hash-value tx-hash-value-mono tx-hash-copy"
              onClick={() => handleCopy(sessionTxId)}
              title={sessionTxId}
              aria-label="Copy Session TXID"
            >
              {sessionTxId}
              <span aria-live="polite">{copied === sessionTxId ? ' ✓' : ''}</span>
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {isPending && stepIndex >= 0 && (
        <div className="status-progress">
          {steps.map((step, i) => (
            <div key={step} className={`progress-step ${i < stepIndex ? 'done' : ''} ${i === stepIndex ? 'active' : ''}`}>
              <div className="step-dot" style={i <= stepIndex ? { background: statusInfo.color, boxShadow: `0 0 8px ${statusInfo.color}50` } : {}}>
                {i < stepIndex ? '✓' : i + 1}
              </div>
              <span className="step-label">{step}</span>
            </div>
          ))}
        </div>
      )}

      {/* Deposit Section */}
      {needsDeposit && payinAddress && (
        <div className="deposit-card">
          <div className="deposit-header">
            Send exactly <strong>{expectedAmountFrom} {fromCurrency?.toUpperCase()}</strong> to:
          </div>
          <div className="deposit-qr-row">
            <div className="deposit-qr">
              <QRCodeSVG value={payinAddress} size={100} level="M" bgColor="#ffffff" fgColor="#06080f" />
            </div>
            <div className="deposit-address-col">
              <div className="deposit-address">{payinAddress}</div>
              <button onClick={() => handleCopy(payinAddress)} className="copy-btn">
                {copied === payinAddress ? 'Copied!' : 'Copy Address'}
              </button>
              {payinExtraId && (
                <div className="deposit-memo-block">
                  <span>Deposit memo / extra ID</span>
                  <button
                    type="button"
                    className="copy-btn deposit-memo-copy"
                    onClick={() => handleCopy(payinExtraId)}
                    aria-label="Copy deposit memo"
                  >
                    {payinExtraId}{copied === payinExtraId ? ' - Copied!' : ''}
                  </button>
                </div>
              )}
            </div>
          </div>
          {payinExtraId && (
            <div className="deposit-warning deposit-warning-critical">
              Both the address and memo are required. Missing the memo can permanently lose your deposit.
            </div>
          )}
          <div className="deposit-warning">
            Send only {fromCurrency?.toUpperCase()}. Other currencies will be lost.
          </div>
        </div>
      )}

      {/* Completed deposit address (no QR) */}
      {!needsDeposit && payinAddress && (
        <div className="deposit-card deposit-card-used">
          <div className="deposit-header">Deposit Address</div>
          <div className="deposit-address">{payinAddress}</div>
        </div>
      )}

      {/* Exchange Info */}
      <div className="exchange-info-card">
        <div className="exchange-pair">
          <span className="exchange-from">{displayAmountFrom} {fromCurrency?.toUpperCase()}</span>
          <span className="exchange-arrow">→</span>
          <span className="exchange-to">{displayAmountTo != null ? `${displayAmountTo} ${toCurrency?.toUpperCase()}` : `${toCurrency?.toUpperCase()}`}</span>
        </div>

        {/* TX Hashes */}
        <div className="tx-hashes">
          {payinHash && (
            <div className="tx-hash-row">
              <span className="tx-hash-label">Inbound TX</span>
              <button type="button" className="tx-hash-value tx-hash-copy" onClick={() => handleCopy(payinHash)} aria-label="Copy inbound transaction hash">
                {payinHash.substring(0, 20)}...{payinHash.substring(payinHash.length - 8)}
                {copied === payinHash ? ' ✓' : ''}
              </button>
            </div>
          )}
          {payoutHash && (
            <div className="tx-hash-row">
              <span className="tx-hash-label">Outbound TX</span>
              <button type="button" className="tx-hash-value tx-hash-copy" onClick={() => handleCopy(payoutHash)} aria-label="Copy outbound transaction hash">
                {payoutHash.substring(0, 20)}...{payoutHash.substring(payoutHash.length - 8)}
                {copied === payoutHash ? ' ✓' : ''}
              </button>
            </div>
          )}
          {depositReceived && (
            <div className="tx-status-received">Deposit received</div>
          )}
          {payoutSent && (
            <div className="tx-status-sent">Payment sent</div>
          )}
        </div>
      </div>

      {/* Full transaction data */}
      <div className="exchange-info-card">
        <div className="deposit-header">Transaction details</div>
        <div className="tx-details-grid">
          <div className="tx-detail-row"><span className="tx-detail-label">Status</span><span className="tx-detail-value">{currentStatus || '—'}</span></div>
          {expectedAmountFrom != null && <div className="tx-detail-row"><span className="tx-detail-label">Expected send</span><span className="tx-detail-value">{expectedAmountFrom} {fromCurrency?.toUpperCase()}</span></div>}
          {amountFrom != null && <div className="tx-detail-row"><span className="tx-detail-label">Actual deposit</span><span className="tx-detail-value">{amountFrom} {fromCurrency?.toUpperCase()}</span></div>}
          {expectedAmountTo != null && <div className="tx-detail-row"><span className="tx-detail-label">Expected receive</span><span className="tx-detail-value">{expectedAmountTo} {toCurrency?.toUpperCase()}</span></div>}
          {amountTo != null && <div className="tx-detail-row"><span className="tx-detail-label">Actual payout</span><span className="tx-detail-value">{amountTo} {toCurrency?.toUpperCase()}</span></div>}
          {completeTransactionData.fromNetwork && <div className="tx-detail-row"><span className="tx-detail-label">Source network</span><span className="tx-detail-value">{completeTransactionData.fromNetwork}</span></div>}
          {completeTransactionData.toNetwork && <div className="tx-detail-row"><span className="tx-detail-label">Destination network</span><span className="tx-detail-value">{completeTransactionData.toNetwork}</span></div>}
          <div className="tx-detail-row"><span className="tx-detail-label">Deposit address</span><span className="tx-detail-value tx-detail-value-mono">{payinAddress || '—'}</span></div>
          {payinExtraId && <div className="tx-detail-row"><span className="tx-detail-label">Deposit memo / extra ID</span><span className="tx-detail-value tx-detail-value-mono">{payinExtraId}</span></div>}
          <div className="tx-detail-row"><span className="tx-detail-label">Destination address</span><span className="tx-detail-value tx-detail-value-mono">{payoutAddress || '—'}</span></div>
          {completeTransactionData.payoutExtraId && <div className="tx-detail-row"><span className="tx-detail-label">Destination memo / extra ID</span><span className="tx-detail-value tx-detail-value-mono">{completeTransactionData.payoutExtraId}</span></div>}
          {completeTransactionData.refundAddress && <div className="tx-detail-row"><span className="tx-detail-label">Refund address</span><span className="tx-detail-value tx-detail-value-mono">{completeTransactionData.refundAddress}</span></div>}
          {completeTransactionData.refundExtraId && <div className="tx-detail-row"><span className="tx-detail-label">Refund memo / extra ID</span><span className="tx-detail-value tx-detail-value-mono">{completeTransactionData.refundExtraId}</span></div>}
          {payinHash && <div className="tx-detail-row"><span className="tx-detail-label">Inbound TX</span><span className="tx-detail-value tx-detail-value-mono">{payinHash}</span></div>}
          {payoutHash && <div className="tx-detail-row"><span className="tx-detail-label">Outbound TX</span><span className="tx-detail-value tx-detail-value-mono">{payoutHash}</span></div>}
          {completeTransactionData.refundHash && <div className="tx-detail-row"><span className="tx-detail-label">Refund TX</span><span className="tx-detail-value tx-detail-value-mono">{completeTransactionData.refundHash}</span></div>}
          {completeTransactionData.refundAmount != null && <div className="tx-detail-row"><span className="tx-detail-label">Refund amount</span><span className="tx-detail-value">{completeTransactionData.refundAmount} {fromCurrency?.toUpperCase()}</span></div>}
          {createdAt && <div className="tx-detail-row"><span className="tx-detail-label">Created</span><span className="tx-detail-value">{new Date(createdAt).toLocaleString()}</span></div>}
          {updatedAt && <div className="tx-detail-row"><span className="tx-detail-label">Updated</span><span className="tx-detail-value">{new Date(updatedAt).toLocaleString()}</span></div>}
          {depositReceivedAt && <div className="tx-detail-row"><span className="tx-detail-label">Deposit received at</span><span className="tx-detail-value">{new Date(depositReceivedAt).toLocaleString()}</span></div>}
          {completeTransactionData.validUntil && <div className="tx-detail-row"><span className="tx-detail-label">Valid until</span><span className="tx-detail-value">{new Date(completeTransactionData.validUntil).toLocaleString()}</span></div>}
          <div className="tx-detail-row"><span className="tx-detail-label">Deposit received</span><span className="tx-detail-value" data-field="deposit-received">{depositReceived ? 'Yes' : 'No'}</span></div>
          <div className="tx-detail-row"><span className="tx-detail-label">Payout sent</span><span className="tx-detail-value" data-field="payout-sent">{payoutSent ? 'Yes' : 'No'}</span></div>
          {typeof completeTransactionData.actionsAvailable === 'boolean' && <div className="tx-detail-row"><span className="tx-detail-label">Actions available</span><span className="tx-detail-value">{completeTransactionData.actionsAvailable ? 'Yes' : 'No'}</span></div>}
        </div>
        <details className="tx-raw-details">
          <summary>Complete provider data</summary>
          <pre data-testid="complete-transaction-data">{JSON.stringify(completeTransactionData, null, 2)}</pre>
        </details>
      </div>

      {/* Completed Message */}
      {isCompleted && (
        <div className="status-message status-success">
          Exchange completed successfully
        </div>
      )}

      {/* Failed Message */}
      {isFailed && (
        <div className="status-message status-error">
          {normalizedStatus === 'expired' ? 'Transaction expired — deposit not received in time' : 'Exchange failed — please contact support'}
        </div>
      )}

      {/* Error */}
      {copyError && (
        <div className="status-message status-error" role="alert">
          {copyError}
        </div>
      )}
      {error && (
        <div className="status-message status-error" role="alert">
          {error}
        </div>
      )}

      {/* Refresh */}
      <button onClick={onRefresh} disabled={isLoading} className="refresh-btn">
        {isLoading ? 'Refreshing...' : 'Refresh'}
      </button>
    </div>
  );
};
